import { z } from 'zod';

/**
 * Envelope canónico de realtime (CLAUDE.md §8, ADR-0002).
 *
 * Todo mensaje WSS (cliente↔servidor) viaja con esta forma:
 *   { t: "<event.tag>", p: <payload>, ts: <unix-ms>, id?: <uuid-correlación> }
 *
 * `t` es la dot-notation jerárquica: `domain.action.tense` (ej. `player.move.intent`).
 * `p` se valida con un schema específico según `t` (ver realtime.ts).
 * `ts` es el reloj del emisor en epoch ms; servidor ignora drift >5 s.
 * `id` permite correlacionar intent (cliente) ↔ outcome (servidor).
 */
export const RealtimeEnvelopeSchema = z.object({
  t: z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)+$/, {
      message: 'event tag must be dot.notation lowercase',
    }),
  p: z.unknown(),
  ts: z.number().int().nonnegative(),
  id: z.string().uuid().optional(),
});

export type RealtimeEnvelope = z.infer<typeof RealtimeEnvelopeSchema>;

/**
 * Helper para definir un envelope tipado para un tag concreto.
 * Devuelve un schema que valida que `t` coincide y `p` es del shape esperado.
 *
 *   const PlayerMoveIntent = defineEnvelope("player.move.intent", PlayerMoveIntentPayload);
 *   PlayerMoveIntent.parse(msg);  // valida tag y payload.
 */
export const defineEnvelope = <Tag extends string, P extends z.ZodTypeAny>(tag: Tag, payload: P) =>
  z.object({
    t: z.literal(tag),
    p: payload,
    ts: z.number().int().nonnegative(),
    id: z.string().uuid().optional(),
  });

/** Cap por mensaje individual; cualquier WS message mayor cierra conexión. */
export const MAX_MESSAGE_BYTES = 1024;

/** Cap por frame WebSocket; ADR-0002 + CLAUDE.md §6.5. */
export const MAX_FRAME_BYTES = 16 * 1024;
